'use client';
import { useTranslations as useTranslations$1, useLocale } from 'next-intl';
import { useParams } from 'next/navigation';
import { notFound } from 'next/navigation';
import Link from 'next/link';
import { ArrowLeft, ArrowRight } from '@/components/ui/icons';
import { DocsShell } from '@/components/docs/DocsShell';
import { LearnVideoPlayer } from '@/components/learn/LearnVideoPlayer';
import { YouTubePlayer } from '@/components/YouTubePlayer';
import { toDocsLocale } from '@/lib/docs';
import { parseYouTube } from '@/lib/youtube';
import { getLessonBySlug, getAdjacentLessons } from '../../learn-data';
import { LESSON_PAGE_COPY } from '../../learn-copy';
import { usePublishedOverrides } from '@/i18n/useContentOverrides';
import { applyLessonOverrides, applyLessonPageCopyOverrides } from '@/lib/i18n/learnRouteMessages';
export default function LessonPage() {
    const t = useTranslations$1();
    const params = useParams<{
        slug: string;
    }>();
    const locale = toDocsLocale(useLocale());
    const overrides = usePublishedOverrides(locale);
    const copy = applyLessonPageCopyOverrides(LESSON_PAGE_COPY[locale], overrides);
    const learnLocale = locale;
    const rawLesson = getLessonBySlug(params.slug, learnLocale);
    const lesson = rawLesson ? applyLessonOverrides(rawLesson, overrides) : rawLesson;
    const adjacent = getAdjacentLessons(params.slug, learnLocale);
    const prev = adjacent.prev ? applyLessonOverrides(adjacent.prev, overrides) : null;
    const next = adjacent.next ? applyLessonOverrides(adjacent.next, overrides) : null;
    if (!lesson) {
        notFound();
    }
    // A YouTube `videoUrl` renders as a native embed; self-hosted MP4s use the
    // custom HTML5 player.
    const yt = parseYouTube(lesson.videoUrl);
    return (<DocsShell>
      {yt ? (<YouTubePlayer key={lesson.slug} videoId={yt.id} startSeconds={yt.start} posterUrl={lesson.thumbnailUrl || undefined} title={lesson.title}/>) : (<LearnVideoPlayer key={lesson.slug} src={lesson.videoUrl} title={lesson.title}/>)}

      <div className="mt-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          { /* The intro is number 0 — labeled "Start here" rather than a lesson number. */ }
          <span>{lesson.number === 0 ? t("xmtnm9") : `${copy.lesson} ${lesson.number}`}</span>
          {lesson.duration && (<>
              { /* eslint-disable-next-line i18next/no-literal-string -- decorative */ }
              <span className="text-muted-foreground/40">&middot;</span>
              <span className="tabular-nums">{lesson.duration}</span>
            </>)}
        </div>

        <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{lesson.title}</h1>

        <p className="mt-2 max-w-2xl text-base leading-relaxed text-muted-foreground">{lesson.description}</p>

        {lesson.guide && (<div className="mt-8 max-w-2xl space-y-7 border-t border-border pt-7 text-sm leading-6 text-muted-foreground">
            {lesson.guide.beforeYouStart && (<section>
                { /* eslint-disable-next-line i18next/no-literal-string -- English guide pending editorial review. */ }
                <h2 className="font-medium text-foreground">Before you start</h2>
                <ul className="mt-2 list-disc space-y-1 ps-5">
                  {lesson.guide.beforeYouStart.map((item, index)=>(// Static, never-reordered copy — index keys are stable and
        // survive two prerequisites sharing the same wording.
        <li key={index}>{item}</li>))}
                </ul>
              </section>)}
            <section>
              { /* eslint-disable-next-line i18next/no-literal-string -- English guide pending editorial review. */ }
              <h2 className="font-medium text-foreground">Follow along</h2>
              <ol className="mt-2 list-decimal space-y-2 ps-5">
                {lesson.guide.steps.map((step, index)=>(// Static, never-reordered copy — see the note above.
        <li key={index}>{step}</li>))}
              </ol>
            </section>
            <section>
              { /* eslint-disable-next-line i18next/no-literal-string -- English guide pending editorial review. */ }
              <h2 className="font-medium text-foreground">You are done when</h2>
              <p className="mt-2">{lesson.guide.success}</p>
            </section>
          </div>)}
      </div>

      <nav className="mt-10 flex items-stretch gap-4 border-t border-border pt-8">
        {prev ? (<Link href={`/learn/tutorials/${prev.slug}`} className="group flex flex-1 flex-col rounded-lg border border-border/30 bg-card/20 px-5 py-4 transition-all hover:border-border/60 hover:bg-card/40">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ArrowLeft className="h-3 w-3 transition-transform group-hover:-translate-x-0.5"/>
              {copy.previous}
            </span>
            <span className="mt-1 text-sm font-medium text-foreground">{prev.title}</span>
          </Link>) : (<div className="flex-1"/>)}

        {next ? (<Link href={`/learn/tutorials/${next.slug}`} className="group flex flex-1 flex-col items-end rounded-lg border border-border/30 bg-card/20 px-5 py-4 text-end transition-all hover:border-border/60 hover:bg-card/40">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {copy.next}
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5"/>
            </span>
            <span className="mt-1 text-sm font-medium text-foreground">{next.title}</span>
          </Link>) : (<Link href="/learn/community" className="group flex flex-1 flex-col items-end rounded-lg border border-border/30 bg-card/20 px-5 py-4 text-end transition-all hover:border-border/60 hover:bg-card/40">
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {copy.next}
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5"/>
            </span>
            <span className="mt-1 text-sm font-medium text-foreground">{copy.community}</span>
          </Link>)}
      </nav>
    </DocsShell>);
}
